import { useCallback, useEffect, useRef, useState } from "react";

export const SOCKET_CONNECTION = {
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
  DISCONNECTED: "disconnected",
};

// Tidak tersambung lebih lama dari ini -> tampilkan modal; library tetap mencoba.
const RECONNECT_GIVE_UP_MS = 60 * 1000;
// Batas reconnect manual setelah server memutus koneksi (mis. token ditolak),
// supaya tidak berulang tanpa henti.
const SERVER_DISCONNECT_RETRY_LIMIT = 3;
const SERVER_DISCONNECT_RETRY_WINDOW_MS = 60 * 1000;
const SERVER_DISCONNECT_RETRY_DELAY_MS = 2000;

/**
 * Melacak status koneksi socket konduktor dan menyambung ulang otomatis.
 *
 * - Tab tidak aktif lama membuat browser memperlambat timer, ping socket.io
 *   terlambat, dan server memutus koneksi. Saat tab aktif lagi, koneksi yang
 *   putus langsung disambung ulang.
 * - socket.io-client v2 tidak reconnect sendiri bila server yang memutus
 *   ("io server disconnect", mis. pod socket restart/deploy); dilakukan di sini.
 * - Gagal tersambung lebih dari RECONNECT_GIVE_UP_MS -> failed = true.
 *
 * @param {object} socket instance socket.io-client
 * @param {boolean} enabled false setelah lelang ditutup (socket sengaja diputus)
 */
const useSocketConnection = (socket, enabled) => {
  const [status, setStatus] = useState(
    socket?.connected ? SOCKET_CONNECTION.CONNECTED : SOCKET_CONNECTION.RECONNECTING
  );
  const [failed, setFailed] = useState(false);
  const enabledRef = useRef(enabled);
  const giveUpTimerRef = useRef(null);
  const serverDisconnectsRef = useRef([]);

  enabledRef.current = enabled;

  const reconnect = useCallback(() => {
    if (!socket || socket.connected || !enabledRef.current) return;
    setFailed(false);
    setStatus(SOCKET_CONNECTION.RECONNECTING);
    socket.connect();
  }, [socket]);

  useEffect(() => {
    if (!socket) return undefined;

    const clearGiveUpTimer = () => {
      if (!giveUpTimerRef.current) return;
      clearTimeout(giveUpTimerRef.current);
      giveUpTimerRef.current = null;
    };

    const startGiveUpTimer = () => {
      if (giveUpTimerRef.current) return;
      giveUpTimerRef.current = setTimeout(() => {
        giveUpTimerRef.current = null;
        if (!socket.connected && enabledRef.current) setFailed(true);
      }, RECONNECT_GIVE_UP_MS);
    };

    const retryAfterServerDisconnect = () => {
      const now = Date.now();
      serverDisconnectsRef.current = serverDisconnectsRef.current.filter(
        (t) => now - t < SERVER_DISCONNECT_RETRY_WINDOW_MS
      );
      if (serverDisconnectsRef.current.length >= SERVER_DISCONNECT_RETRY_LIMIT) {
        setFailed(true);
        return;
      }
      serverDisconnectsRef.current.push(now);
      setTimeout(() => {
        if (!socket.connected && enabledRef.current) socket.connect();
      }, SERVER_DISCONNECT_RETRY_DELAY_MS);
    };

    const handleConnect = () => {
      clearGiveUpTimer();
      setFailed(false);
      setStatus(SOCKET_CONNECTION.CONNECTED);
    };

    const handleDisconnect = (reason) => {
      console.log(`🔌 Socket terputus: ${reason}`);
      // Diputus sendiri oleh aplikasi (lelang selesai / unmount), bukan gangguan.
      if (reason === "io client disconnect") return;
      if (!enabledRef.current) return;
      setStatus(SOCKET_CONNECTION.RECONNECTING);
      startGiveUpTimer();
      if (reason === "io server disconnect") retryAfterServerDisconnect();
    };

    const handleReconnectAttempt = () => {
      if (!enabledRef.current) return;
      setStatus(SOCKET_CONNECTION.RECONNECTING);
    };

    const reconnectIfNeeded = () => {
      if (socket.connected || !enabledRef.current) return;
      setStatus(SOCKET_CONNECTION.RECONNECTING);
      socket.connect();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") reconnectIfNeeded();
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("reconnect_attempt", handleReconnectAttempt);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", reconnectIfNeeded);
    window.addEventListener("online", reconnectIfNeeded);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("reconnect_attempt", handleReconnectAttempt);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", reconnectIfNeeded);
      window.removeEventListener("online", reconnectIfNeeded);
      clearGiveUpTimer();
    };
  }, [socket]);

  const currentStatus = failed ? SOCKET_CONNECTION.DISCONNECTED : status;
  return { status: currentStatus, failed: failed && enabled, reconnect };
};

export default useSocketConnection;
